<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/testing/field">Field Tests</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit' : 'New' }} Field Test</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Field Test' : 'New Field Test' }}</h1>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading test data...</p>
</div>

<!-- Form -->
<form *ngIf="!isLoading" [formGroup]="testForm" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column: Test Details -->
    <div class="col-lg-8">
      <!-- Basic Info Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Test Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <!-- Test Type -->
            <div class="col-md-6">
              <label class="form-label">Test Type <span class="text-danger">*</span></label>
              <div class="test-type-selector">
                <div *ngFor="let type of testTypes" class="test-type-option"
                     [class.selected]="testForm.get('testType')?.value === type.value"
                     (click)="testForm.patchValue({ testType: type.value })">
                  <i class="bi" [ngClass]="type.icon"></i>
                  <span>{{ type.label }}</span>
                </div>
              </div>
            </div>

            <!-- Title -->
            <div class="col-md-6">
              <label class="form-label">Test Title <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="title"
                     placeholder="e.g., Pre-Flight Check - DJI Agras T40">
              <div class="form-text">Auto-generated based on type and aircraft, or enter custom title</div>
            </div>

            <!-- Aircraft -->
            <div class="col-md-6">
              <label class="form-label">Aircraft/Equipment <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="aircraft" (change)="onAircraftChange()">
                <option value="">Select aircraft...</option>
                <option *ngFor="let aircraft of aircraftList" [value]="aircraft.id">
                  {{ aircraft.name }}
                </option>
              </select>
            </div>

            <!-- Pilot -->
            <div class="col-md-6">
              <label class="form-label">Performed By <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="pilot">
                <option value="">Select pilot...</option>
                <option *ngFor="let pilot of pilotList" [value]="pilot.id">
                  {{ pilot.name }}
                </option>
              </select>
            </div>

            <!-- Date -->
            <div class="col-md-6">
              <label class="form-label">Date <span class="text-danger">*</span></label>
              <input type="date" class="form-control" formControlName="date">
            </div>

            <!-- Location -->
            <div class="col-md-6">
              <label class="form-label">Location <span class="text-danger">*</span></label>
              <!-- Dropdown for selecting location -->
              <div *ngIf="!showCustomLocation">
                <select class="form-select" formControlName="location" (change)="onLocationChange()">
                  <option value="">Select location...</option>
                  <option *ngFor="let loc of locationOptions" [value]="loc.value">
                    {{ loc.label }}
                  </option>
                  <option value="__other__">Other (enter manually)...</option>
                </select>
              </div>
              <!-- Custom location input -->
              <div *ngIf="showCustomLocation">
                <div class="input-group">
                  <input type="text" class="form-control" formControlName="location"
                         placeholder="e.g., Home Base, Warehouse, etc.">
                  <button type="button" class="btn btn-outline-secondary" (click)="cancelCustomLocation()"
                          title="Back to dropdown">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
                <div class="form-text">Enter a custom location</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Checklist Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-list-check me-2"></i>Checklist</h5>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-outline-success" (click)="toggleAllPassed(true)"
                      [disabled]="checklist.length === 0">
                <i class="bi bi-check-all me-1"></i>Pass All
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleAllPassed(false)"
                      [disabled]="checklist.length === 0">
                <i class="bi bi-x-lg me-1"></i>Reset All
              </button>
              <button type="button" class="btn btn-sm btn-outline-primary" (click)="addChecklistItem()">
                <i class="bi bi-plus-lg me-1"></i>Add Item
              </button>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <!-- Progress bar -->
          <div class="checklist-progress p-3 border-bottom" *ngIf="checklist.length > 0">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="small">Progress: {{ passedCount }} of {{ checklist.length }} passed</span>
              <span class="badge" [ngClass]="{
                'bg-success': overallStatus === 'passed',
                'bg-danger': overallStatus === 'failed',
                'bg-warning': overallStatus === 'pending'
              }">
                {{ overallStatus | titlecase }}
              </span>
            </div>
            <div class="progress h-8px">
              <div class="progress-bar bg-success" [style.width.%]="(passedCount / checklist.length) * 100"></div>
            </div>
          </div>

          <!-- Checklist Items -->
          <div class="checklist-items" formArrayName="checklist">
            <div *ngFor="let item of checklist.controls; let i = index; let first = first; let last = last"
                 class="checklist-item" [formGroupName]="i"
                 [class.item-passed]="item.get('passed')?.value"
                 [class.item-failed]="!item.get('passed')?.value">
              <div class="d-flex align-items-start gap-3">
                <!-- Pass/Fail Toggle -->
                <div class="pass-toggle" (click)="item.patchValue({ passed: !item.get('passed')?.value })">
                  <div class="toggle-btn" [class.passed]="item.get('passed')?.value">
                    <i class="bi" [class.bi-check-lg]="item.get('passed')?.value"
                       [class.bi-x-lg]="!item.get('passed')?.value"></i>
                  </div>
                </div>

                <!-- Item Content -->
                <div class="flex-grow-1">
                  <div class="row g-2">
                    <div class="col-12">
                      <input type="text" class="form-control form-control-sm" formControlName="item"
                             placeholder="Checklist item description">
                    </div>
                    <div class="col-12">
                      <input type="text" class="form-control form-control-sm notes-input" formControlName="notes"
                             placeholder="Notes (optional)">
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                <div class="item-actions">
                  <button type="button" class="btn btn-sm btn-ghost" (click)="moveChecklistItem(i, 'up')"
                          [disabled]="first" title="Move up">
                    <i class="bi bi-chevron-up"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" (click)="moveChecklistItem(i, 'down')"
                          [disabled]="last" title="Move down">
                    <i class="bi bi-chevron-down"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost text-danger" (click)="removeChecklistItem(i)"
                          title="Remove">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div *ngIf="checklist.length === 0" class="text-center py-5">
              <i class="bi bi-list-check text-muted fs-2-5rem"></i>
              <p class="text-muted mt-2 mb-3">No checklist items</p>
              <button type="button" class="btn btn-primary btn-sm" (click)="addChecklistItem()">
                <i class="bi bi-plus-lg me-1"></i>Add First Item
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Notes Card -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Additional Notes</h5>
        </div>
        <div class="card-body">
          <textarea appCharCounter class="form-control" formControlName="notes" rows="4"
                    placeholder="Any additional observations, issues, or recommendations..."></textarea>
        </div>
      </div>
    </div>

    <!-- Right Column: Summary & Actions -->
    <div class="col-lg-4">
      <!-- Summary Card -->
      <div class="card mb-4 summary-card">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-clipboard-data me-2"></i>Summary</h5>
        </div>
        <div class="card-body">
          <!-- Status Display -->
          <div class="status-display mb-4 text-center"
               [class.status-passed]="overallStatus === 'passed'"
               [class.status-failed]="overallStatus === 'failed'"
               [class.status-pending]="overallStatus === 'pending'">
            <div class="status-icon">
              <i class="bi" [ngClass]="{
                'bi-check-circle-fill': overallStatus === 'passed',
                'bi-x-circle-fill': overallStatus === 'failed',
                'bi-clock-fill': overallStatus === 'pending'
              }"></i>
            </div>
            <div class="status-label">{{ overallStatus | titlecase }}</div>
            <div class="status-detail" *ngIf="checklist.length > 0">
              {{ passedCount }}/{{ checklist.length }} items passed
            </div>
          </div>

          <!-- Test Details -->
          <div class="summary-list">
            <div class="summary-item" *ngIf="testForm.get('testType')?.value">
              <span class="summary-label">Type</span>
              <span class="summary-value">
                <i class="me-1" [ngClass]="getTestTypeIcon(testForm.get('testType')?.value)"></i>
                {{ getTestTypeLabel() }}
              </span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('aircraft')?.value">
              <span class="summary-label">Aircraft</span>
              <span class="summary-value">
                {{ getAircraftName() }}
              </span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('pilot')?.value">
              <span class="summary-label">Performed By</span>
              <span class="summary-value">
                {{ getPilotName() }}
              </span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('date')?.value">
              <span class="summary-label">Date</span>
              <span class="summary-value">{{ testForm.get('date')?.value }}</span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('location')?.value">
              <span class="summary-label">Location</span>
              <span class="summary-value">{{ testForm.get('location')?.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Templates Card -->
      <div class="card mb-4" *ngIf="!isEditMode">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-lightning me-2"></i>Quick Templates</h5>
        </div>
        <div class="card-body">
          <p class="small text-muted mb-3">Load a pre-defined checklist template:</p>
          <div class="d-grid gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm text-start"
                    *ngFor="let template of testTypes"
                    (click)="testForm.patchValue({ testType: template.value })">
              <i class="bi me-2" [ngClass]="template.icon"></i>{{ template.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Actions Card -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-save me-2"></i>Actions</h5>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving || testForm.invalid">
              <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!isSaving" class="bi bi-check-lg me-2"></i>
              {{ isSaving ? 'Saving...' : (isEditMode ? 'Update Test' : 'Save Test') }}
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</form>
